import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ExternalLink, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cPath } from "../../constants";
import { normalizeStoreLogoUrl } from "../../utils/storeLogo";

const CardLink = styled.a`
  text-decoration: none;
  color: inherit;
  display: block;
`;

const Card = styled.div`
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius["2xl"] || theme.radius.xl};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  padding: 0;
  box-shadow: ${({ theme }) => theme.shadow.cardSm || theme.shadow.cardMd};
  display: flex;
  flex-direction: column;
  width: 200px;
  height: 200px;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadow.cardMd};
    border-color: hsl(${({ theme }) => theme.colors.primary} / 0.25);
  }
  @media (max-width: 768px) {
    width: calc((100% - 10px) / 2);
    min-width: 0;
    height: 160px;
  }
`;

const LogoWrap = styled.div`
  position: relative;
  width: 100%;
  height: 120px;
  background: hsl(${({ theme }) => theme.colors.muted});
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  @media (max-width: 768px) {
    height: 90px;
  }
`;

const Logo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 0.5rem;
  transition: transform 0.15s ease;
  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const VisitIcon = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 2rem;
  height: 2rem;
  border-radius: ${({ theme }) => theme.radius.full};
  background: hsl(${({ theme }) => theme.colors.primary});
  color: hsl(${({ theme }) => theme.colors.primaryForeground || theme.colors.card});
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: scale(0);
  transition: all 0.15s ease;
  ${Card}:hover & {
    opacity: 1;
    transform: scale(1);
  }
`;

const Content = styled.div`
  text-align: center;
  padding: 0.5rem 0.75rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 80px;
`;

const Name = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: hsl(${({ theme }) => theme.colors.foreground});
  margin: 0 0 0.5rem 0;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const Stats = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: auto;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

function safeRating(store) {
  const raw = Number(store?.rating);
  if (Number.isFinite(raw) && raw > 0) return raw.toFixed(1);
  return "4.5";
}

export default function StoreCard({ store }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const count = Number(store?.couponCount || 0);
  const couponLabel = count === 1
    ? `${count} ${t("store.couponSingular")}`
    : `${count} ${t("store.couponPlural")}`;
  const href = cPath(`store/${store.slug}`);

  const handleClick = (e) => {
    if (e.target.closest("button") || e.target.closest("a")) return;
    e.preventDefault();
    navigate(href);
  };

  return (
    <CardLink href={href} onClick={handleClick}>
      <Card
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick(e);
          }
        }}
      >
        <LogoWrap>
          <Logo src={normalizeStoreLogoUrl(store.logo)} alt={store.name} />
          <VisitIcon>
            <ExternalLink size={14} />
          </VisitIcon>
        </LogoWrap>
        <Content>
          <Name>{store.name}</Name>
          <Stats>
            <StatItem>
              <Star size={12} />
              <span>{safeRating(store)}</span>
            </StatItem>
            <StatItem>
              <span>{couponLabel}</span>
            </StatItem>
          </Stats>
        </Content>
      </Card>
    </CardLink>
  );
}
